/* eslint-disable no-param-reassign */
import React from 'react';
import Icon, { IconSprite } from 'components/Icon/Icon';
import { getFileNameFromURL, randomTempIdGenerator } from 'utils/utils';
import { v4 as uuidv4 } from 'uuid';
import { isEmpty } from 'ramda';
import uploadAWSS3File from 'utils/uploadAWSS3File';
import { dequal } from 'dequal';
import { Link } from 'react-router-dom';
import { TreatmentNote } from 'pages/TreatmentPlan/TreatmentPlan';
import useValidator from '../../../hooks/useValidator';
import Modal from '../Modal';
import TextBoxInput from '../../Inputs/TextBoxInput/TextBoxInput';
import Button from '../../Button/Button';

type ModalProps = {
  onCloseModal: () => void;
  onAddNote: (note: Partial<TreatmentNote>) => void;
  isAddingNote: boolean;
  initialNote: {
    notesForPatient: string;
    notesForGuide: string;
  };
  attachments?: string[];
};

type AttachedFile = {
  id: string;
  name: string;
  key: string;
  url: string;
  file?: File;
  tempURL?: string;
  isUploadingFile?: boolean;
};

function getFileKey(filename: string) {
  return `notes/${uuidv4()}/${filename}`;
}

function initialAttachFiles(attachments?: string[]) {
  if (!attachments || attachments.length === 0) {
    return undefined;
  }

  return attachments.map(file => ({
    id: randomTempIdGenerator(),
    name: decodeURIComponent(getFileNameFromURL(file.split('?')[0])),
    key: getFileKey(decodeURIComponent(getFileNameFromURL(file.split('?')[0]))),
    url: file,
    tempURL: file,
    isUploadingFile: false,
  }));
}

function TreatmentNotesForm({
  onCloseModal,
  onAddNote,
  isAddingNote,
  initialNote,
  attachments,
}: ModalProps) {
  const [validator, showValidationMessage] = useValidator({});
  const [attachFiles, setAttachFiles] = React.useState<AttachedFile[] | undefined>(() =>
    initialAttachFiles(attachments),
  );
  const [note, setNote] = React.useState(initialNote);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;

    setNote(fndNote => ({
      ...fndNote,
      [name]: value,
    }));
  };

  const handleAttachmentUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files || event.target.files.length === 0) return;

    let files: AttachedFile[] = (Array.from(event.target.files) || []).map(file => ({
      file,
      id: `${file.lastModified}${file.size}`,
      name: file.name,
      key: getFileKey(file.name),
      url: '',
      tempURL: URL.createObjectURL(file),
      isUploadingFile: true,
    }));

    files = [...(attachFiles || []), ...files];

    setAttachFiles(files);

    const uploadedFiles = await Promise.all(
      files.map(async findAttached => {
        if ((findAttached.url && !isEmpty(findAttached.url)) || !findAttached.file) {
          return {
            ...findAttached,
            isUploadingFile: false,
          };
        }

        const resizedFile = findAttached.file;

        return {
          ...findAttached,
          url: await uploadAWSS3File(resizedFile, findAttached.key, resizedFile.type, 'public'),
          isUploadingFile: false,
        };
      }),
    );

    if (!dequal(files, uploadedFiles)) {
      setAttachFiles(uploadedFiles);
    }

    event.target.files = null;
    event.target.value = '';
  };

  const handleAddNote = async () => {
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }

    const getAttachments = (attachFiles || []).map(fndFile =>
      decodeURIComponent(fndFile.url.replace(/\?.*$/, '')),
    );

    const payload = {
      notesForGuide: note.notesForGuide,
      notesForPatient: note.notesForPatient,
      attachments: getAttachments.length > 0 ? getAttachments : null,
    };

    onAddNote(payload);
  };

  return (
    <Modal onClose={onCloseModal} show>
      <div className="rounded-lg bg-white tablet:w-[500px] space-y-4 pt-8 pb-8 px-8">
        <div className="flex flex-col">
          <h3 className="text-lg font-semibold inline-flex whitespace-pre mb-2">Provider notes</h3>

          <div className="fs-exclude space-y-3">
            <TextBoxInput
              name="notesForGuide"
              placeholder="Add note here..."
              value={note.notesForGuide}
              onChange={handleInputChange}
              rows={5}
              className="bg-[#FAF9F9] border-[1px] border-[#C5CCD0] focus:bg-white 
              peer-[.validator-error]/notesForGuide:!border-river-red peer-[.validator-error]/notesForGuide:!mb-5 
              peer-[.validator-error]/notesForGuide:focus:!ring-river-red"
            />
          </div>
        </div>

        <div className="flex flex-col">
          <h3 className="text-lg font-semibold inline-flex whitespace-pre mb-2">
            Notes to patient
          </h3>

          <div className="fs-exclude space-y-3">
            <TextBoxInput
              name="notesForPatient"
              placeholder="Add note here..."
              value={note.notesForPatient}
              onChange={handleInputChange}
              rows={5}
              className="bg-[#FAF9F9] border-[1px] border-[#C5CCD0] focus:bg-white  
                peer-[.validator-error]/notesForPatient:!border-river-red peer-[.validator-error]/notesForPatient:!mb-5 
                peer-[.validator-error]/notesForPatient:focus:!ring-river-red"
              validator={validator.message(
                'notesForPatient',
                note.notesForPatient,
                'required|string|min:2',
                {
                  messages: {
                    required: 'Text required to add notes for patient.',
                  },
                  className: 'peer/notesForPatient validator-error mt-3 top-[150px] ml-1',
                },
              )}
            />
          </div>
        </div>

        <div className="!mt-2 flex flex-row gap-4 flex-wrap">
          {attachFiles ? (
            <>
              {(attachFiles || []).map(file => (
                <div
                  className="flex flex-row items-center border border-[#C4C9CC] rounded-full px-3 py-2"
                  key={file.id}
                >
                  <Link
                    to={file.tempURL || file.url}
                    title={file.name}
                    target="_blank"
                    className="text-ellipsis overflow-hidden text-xs leading-snug w-[150px] whitespace-pre 
                    hover:underline hover:text-river-blue underline-offset-2"
                  >
                    {file.name}
                  </Link>

                  <button
                    type="button"
                    onClick={() => {
                      setAttachFiles(fndFiles =>
                        fndFiles?.filter(fndFile => fndFile.id !== file.id),
                      );
                    }}
                    className="bg-[#EAEEF0] rounded-full w-5 h-5 flex flex-row justify-center items-center ml-2"
                  >
                    {file.isUploadingFile ? (
                      <Icon id="loader" className="w-[18px] h-[18px] text-river-blue" />
                    ) : (
                      <Icon id="close" className="w-2 h-2" />
                    )}
                  </button>
                </div>
              ))}

              <button
                type="button"
                className="relative rounded-full flex flex-row justify-center text-river-blue font-medium
                items-center border border-river-blue px-[14px] py-2 text-xs leading-snug"
              >
                <Icon id="addPlusSign" className="w-3 h-3 text-river-blue fill-river-blue mr-2" />
                Add New
                <input
                  type="file"
                  accept="image/*,.doc,.docx,.pdf"
                  className="absolute inset-0 w-full bg-slate-400 h-auto opacity-0 file:cursor-pointer hover:cursor-pointer 
              disabled:!border-[#dfe5eb] disabled:!bg-[#F9F9F9] disabled:hover:cursor-not-allowed disabled:!cursor-not-allowed"
                  onChange={handleAttachmentUpload}
                />
              </button>
            </>
          ) : (
            <button
              type="button"
              className="relative flex-none border border-gray-300 
            w-full h-[46px] py-2 px-3 rounded-md shadow-none hover:ring-[0.5px]  text-river-black
            flex items-center justify-start cursor-pointer hover:border-river-blue hover:ring-river-blue overflow-hidden 
            disabled:hover:border-gray-300 disabled:hover:ring-gray-300 disabled:hover:ring-transparent disabled:!cursor-not-allowed"
            >
              <IconSprite
                id="icon-paperclip"
                className="w-[20px] h-[20px] text-[#C2C2C2] cursor-pointer shrink-0"
              />

              <span className="font-light text-[15px] whitespace-pre cursor-pointer overflow-hidden text-ellipsis ml-1 text-gray-300">
                Click here to attach files
              </span>
              <input
                type="file"
                multiple
                accept="image/*,.doc,.docx,.pdf"
                className="absolute inset-0 w-full bg-slate-400 h-[46px] opacity-0 file:cursor-pointer hover:cursor-pointer 
              disabled:!border-[#dfe5eb] disabled:!bg-[#F9F9F9] disabled:hover:cursor-not-allowed disabled:!cursor-not-allowed"
                onChange={handleAttachmentUpload}
              />
            </button>
          )}
        </div>

        <div
          className="!mt-8 flex flex-row items-center justify-center 
            laptop:justify-end w-full flex-wrap-reverse"
        >
          <Button
            label={note.notesForPatient ? 'Update note' : 'Add note'}
            onClick={handleAddNote}
            className="w-[200px] !rounded-full !py-[10px]"
            disabled={isAddingNote}
            loading={isAddingNote}
          />
        </div>
      </div>
    </Modal>
  );
}

export default TreatmentNotesForm;
